import makeStyles from '@mui/styles/makeStyles';
import {difference, filter, isEmpty, isUndefined, map} from 'lodash';
import React, {useEffect} from 'react';
import {useTranslation} from "react-i18next";
import {Redirect, Route, Switch, useHistory} from "react-router-dom";
import {isSurveyComplete} from "business/survey";
import {color, space, type} from "common/tokens";
import useFreshData, {dataKeys} from "common/useFreshData";
import Accordion from "components/redesign/Accordion";
import Skeleton from "components/redesign/Skeleton";
import SurveyCard from "components/redesign/SurveyCard";

const useStyles = makeStyles(theme => ({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '48px 32px 64px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    [theme.breakpoints.down('md')]: {
      padding: '24px 16px 48px',
    },
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  empty: {
    ...type.body,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: '80px 0',
  },
  skeletons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
}));

const SurveysListing = ({getAllSurveys, surveys, setCurrentPath}) => {
  const classes = useStyles();
  const history = useHistory();
  const {t} = useTranslation();

  useEffect(() => {
    setCurrentPath('survey');
  }, [setCurrentPath]);

  useFreshData(dataKeys.SURVEYS, getAllSurveys);

  const usableSurveys = filter(surveys, survey => survey.expPermission === 'use');
  const completedSurveys = filter(usableSurveys, isSurveyComplete);
  const pendingSurveys = difference(usableSurveys, completedSurveys);

  const openSurvey = survey => history.push('/survey', {surveyId: survey.id});

  const surveyGrid = list => (
    <div className={classes.grid}>
      {map(list, survey => (
        <SurveyCard key={survey.id} survey={survey} onOpen={() => openSurvey(survey)}/>
      ))}
    </div>
  );

  // `surveys` is undefined until the first response lands, which is what tells a
  // cold open apart from a program that has no surveys.
  const body = isUndefined(surveys) ? (
    <div className={classes.skeletons}>
      <Skeleton width={160} height={14}/>
      <Skeleton height={78}/>
      <Skeleton height={78}/>
    </div>
  ) : isEmpty(usableSurveys) ? (
    <p className={classes.empty}>{t('noSurveysYet')}</p>
  ) : (
    <>
      {!isEmpty(pendingSurveys) && (
        <Accordion label={t('pendingSurveysCaps')} count={pendingSurveys.length} defaultOpen>
          {surveyGrid(pendingSurveys)}
        </Accordion>
      )}
      {/* Completed opens when it is the only section, so a mentee who has
          finished everything does not land on a page of closed bands. */}
      {!isEmpty(completedSurveys) && (
        <Accordion
          label={t('completedSurveysCaps')}
          count={completedSurveys.length}
          defaultOpen={isEmpty(pendingSurveys)}
        >
          {surveyGrid(completedSurveys)}
        </Accordion>
      )}
    </>
  );

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <h1 className={classes.title}>{t('survey')}</h1>
        <Switch>
          <Route exact path={'/surveylisting'}>{body}</Route>
          {/* The two lists live on one page now; the old tabs kept their own
              routes, so links out there still land somewhere. */}
          <Redirect to={'/surveylisting'}/>
        </Switch>
      </div>
    </div>
  );
};

export default SurveysListing;
